"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import {
  Target,
  Eye,
  ShieldCheck,
  Handshake,
  Users,
  Cpu,
  LucideIcon,
} from "lucide-react";

type Valor = {
  key: string;
  title: string;
  description: string;
};

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Fotos%20aereas%20variadas/321A5429.jpg";

const VALOR_ICONS: Record<string, LucideIcon> = {
  responsabilidad: ShieldCheck,
  cliente: Handshake,
  colaboradores: Users,
  excelencia: Cpu,
};

export default function InstitucionalPage() {
  const t = useTranslations("institucionalPage");
  const valores = t.raw("valores") as Valor[];

  return (
    <main className="min-h-screen bg-white">
      {/* Hero con imagen */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("title")}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px bg-white" />
              {t("eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Misión y Visión */}
      <section className="max-w-[80%] mx-auto py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
          <article className="bg-white p-10 md:p-12 flex flex-col">
            <Target className="w-8 h-8 text-[#102542] mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
              {t("mision.title")}
            </h2>
            <p className="text-gray-600 text-base leading-relaxed">
              {t("mision.description")}
            </p>
          </article>
          <article className="bg-white p-10 md:p-12 flex flex-col">
            <Eye className="w-8 h-8 text-[#102542] mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">
              {t("vision.title")}
            </h2>
            <p className="text-gray-600 text-base leading-relaxed">
              {t("vision.description")}
            </p>
          </article>
        </div>
      </section>

      {/* Valores */}
      <section className="max-w-[80%] mx-auto pb-24">
        <div className="max-w-3xl mb-14">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold mb-4">
            <span className="w-8 h-px bg-[#102542]" />
            {t("valoresEyebrow")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            {t("valoresTitle")}
          </h2>
          <p className="text-gray-600 text-base leading-relaxed">
            {t("valoresSubtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
          {valores.map((valor) => {
            const Icon = VALOR_ICONS[valor.key] ?? Target;
            return (
              <article
                key={valor.key}
                className="bg-white p-8 flex flex-col transition-colors hover:bg-gray-50"
              >
                <Icon className="w-6 h-6 text-[#102542] mb-5" />
                <h3 className="text-lg font-semibold text-gray-900 mb-3 tracking-tight">
                  {valor.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {valor.description}
                </p>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}
